// @ts-nocheck
/**
 * モンスターインスタンス生成モジュール
 * コレクションのカードからRPG用モンスターインスタンスを生成
 * @module twinKey/core/party/MonsterInstance
 */

import { calculateAllStats } from './StatCalculator';
import { RARITY_CONFIG } from '../../data/constants';

// 新スキル・パッシブシステム
import { createSkillInstances, getSkillSource, getSkillUnlockStatus } from '../../data/skills';
import { createPassiveInstances, getPassiveSource } from '../../data/passives';

// 装備システム
import { equipmentManager } from '../../features/equipment/EquipmentManager';

// カテゴリ・派閥グループ
import { isCategoryInGroup, isFactionInGroup } from '../../data/categoryGroups';

// レガシーシステム用定数（後方互換性のため）
const SKILL_COOLDOWNS = {
  skill_1: 2,
  skill_2: 3,
  skill_3: 4,
};

/**
 * モンスターインスタンスを生成
 * @param {Object} card - カード情報（コレクションから）
 * @param {Object} progress - 成長データ（playerData.twinKey.monsterProgressから）
 * @returns {Object} MonsterInstance
 */
export function createMonsterInstance(card, progress = null) {
  const level = progress?.level || 1;
  const awakening = progress?.awakening || 0;
  const exp = progress?.exp || 0;
  const rarity = card.rarity || 'C';

  // ステータス計算
  const stats = calculateAllStats(card, level, rarity, awakening);

  // スキル情報を生成（新システム）
  const skills = createSkillInstances(card, level);
  const skillSource = getSkillSource(card);

  // パッシブ情報を生成（新システム）
  const passives = createPassiveInstances(card, awakening);
  const passiveSource = getPassiveSource(card);

  return {
    // カード情報
    cardId: card.id,
    uniqueId: card.uniqueId || card.id,
    uniqueKey: card.uniqueKey || `${card.id}_${rarity}`,
    name: card.name,
    attribute: card.attribute,
    category: card.category || '',
    rarity,

    // 基礎ステータス（カードから）
    baseAttack: card.attack,
    baseHp: card.hp,

    // 成長情報
    level,
    exp,
    awakening,
    maxLevel: RARITY_CONFIG[rarity]?.maxLevel || 10,

    // 計算済みステータス
    stats: {
      attack: stats.attack,
      maxHp: stats.maxHp,
      currentHp: stats.maxHp,  // 初期HPは最大HP
      defense: stats.defense,
      speed: stats.speed,
    },

    // 軽減率（参照用）
    damageReduction: stats.damageReduction,

    // スキル（新システム）
    skills,
    skillSource,

    // パッシブ（新システム）
    passives,
    passiveSource,

    // キーワード能力（Phase 2以降で使用）
    keywords: parseKeywords(card.keyword),

    // 生存状態
    isAlive: true,

    // 装備スロット（オブジェクト形式: { WEAPON, ARMOR, ACCESSORY }）
    equipment: card.equipment || {},
  };
}

/**
 * [LEGACY] スキル情報を生成
 * 新システムでは createSkillInstances (from skills/skillResolver.js) を使用
 * 後方互換性のために残している
 *
 * @deprecated 新システムでは使用しない
 * @param {Object} card - カード情報
 * @param {number} level - 現在レベル
 * @returns {Array} スキル配列
 */
function generateSkillsLegacy(card, level) {
  const unlock = getSkillUnlockStatus(level);

  // カードのeffectフィールドからスキル情報を抽出
  const skillInfo = parseSkillsFromEffect(card.effect);

  return [
    {
      id: 'skill_1',
      name: skillInfo.skill_1?.name || '基本スキル',
      description: skillInfo.skill_1?.description || '',
      unlocked: unlock.skill_1,
      maxCd: SKILL_COOLDOWNS.skill_1,
      type: skillInfo.skill_1?.type || 'single',
    },
    {
      id: 'skill_2',
      name: skillInfo.skill_2?.name || '中級スキル',
      description: skillInfo.skill_2?.description || '',
      unlocked: unlock.skill_2,
      maxCd: SKILL_COOLDOWNS.skill_2,
      type: skillInfo.skill_2?.type || 'single',
    },
    {
      id: 'skill_3',
      name: skillInfo.skill_3?.name || '必殺技',
      description: skillInfo.skill_3?.description || '',
      unlocked: unlock.skill_3,
      maxCd: SKILL_COOLDOWNS.skill_3,
      type: skillInfo.skill_3?.type || 'single',
    },
  ];
}

/**
 * カードのeffectフィールドからスキル情報をパース
 * @param {string} effect - カードのeffectテキスト
 * @returns {Object} スキル情報
 */
function parseSkillsFromEffect(effect) {
  if (!effect) return {};

  const result = {};

  // 基本技パターン
  const basicMatch = effect.match(/基本技[:：]?\s*([^。]+)/);
  if (basicMatch) {
    result.skill_1 = {
      name: '基本技',
      description: basicMatch[1].trim(),
      type: detectSkillType(basicMatch[1]),
    };
  }

  // 上級技パターン
  const advancedMatch = effect.match(/上級技[:：]?\s*([^。]+)/);
  if (advancedMatch) {
    result.skill_2 = {
      name: '上級技',
      description: advancedMatch[1].trim(),
      type: detectSkillType(advancedMatch[1]),
    };
  }

  // 必殺技/究極技パターン（将来用）
  const ultimateMatch = effect.match(/(?:必殺技|究極技)[:：]?\s*([^。]+)/);
  if (ultimateMatch) {
    result.skill_3 = {
      name: '必殺技',
      description: ultimateMatch[1].trim(),
      type: detectSkillType(ultimateMatch[1]),
    };
  }

  return result;
}

/**
 * スキルタイプを検出（単体/全体）
 * @param {string} description - スキル説明
 * @returns {'single' | 'aoe'}
 */
function detectSkillType(description) {
  const aoeKeywords = ['全体', '全員', '敵全て', '相手全て', '味方全て'];
  for (const keyword of aoeKeywords) {
    if (description.includes(keyword)) {
      return 'aoe';
    }
  }
  return 'single';
}

/**
 * キーワード能力をパース
 * @param {string} keywordStr - キーワード文字列
 * @returns {string[]}
 */
function parseKeywords(keywordStr) {
  if (!keywordStr || typeof keywordStr !== 'string') return [];
  const matches = keywordStr.match(/【([^】]+)】/g) || [];
  return matches.map(m => m.replace(/[【】]/g, ''));
}

/**
 * モンスターインスタンスをバトル用に変換
 * 装備によるステータス補正を適用
 * @param {Object} instance - MonsterInstance
 * @param {number} index - パーティ内のインデックス
 * @returns {Object} BattleUnit
 */
export function toBattleUnit(instance, index) {
  // 装備によるステータス補正を計算
  const equipMods = equipmentManager.calculateEquipmentModifiers(instance);

  // 基礎ステータス + 装備ボーナス
  const baseAttack = instance.stats.attack;
  const baseMaxHp = instance.stats.maxHp;
  const baseDefense = instance.stats.defense;
  const baseSpeed = instance.stats.speed;

  const finalAttack = Math.floor(baseAttack * (1 + equipMods.atkPercent) + equipMods.atkFlat);
  const finalMaxHp = Math.floor(baseMaxHp * (1 + equipMods.hpPercent) + equipMods.hpFlat);
  const finalDefense = baseDefense + equipMods.defFlat;
  const finalSpeed = baseSpeed + equipMods.spdFlat;

  // 現在HPを最大HPに比例して調整（装備でmaxHpが増えた場合）
  const hpRatio = instance.stats.currentHp / baseMaxHp;
  const finalCurrentHp = Math.min(Math.floor(finalMaxHp * hpRatio), finalMaxHp);

  return {
    id: `ally_${index}`,
    instanceId: instance.uniqueId,
    name: instance.name,
    isAlly: true,
    attribute: instance.attribute,
    category: instance.category,

    stats: {
      attack: finalAttack,
      maxHp: finalMaxHp,
      currentHp: finalCurrentHp, // パーティの現在HPを保持（装備補正適用）
      defense: finalDefense,
      speed: finalSpeed,
    },

    // 装備ボーナス情報（バトル中の参照用）
    equipmentMods: {
      // クリティカル系
      critRate: equipMods.critRate,
      critDmg: equipMods.critDmg,

      // ダメージ系
      damageBonus: equipMods.damageBonus,
      damageReduce: equipMods.damageReduce,
      skillPower: equipMods.skillPower,

      // 回復・吸収系
      lifesteal: equipMods.lifesteal,
      hpDrain: equipMods.hpDrain,
      healBoost: equipMods.healBoost,

      // 属性系
      attributeBonuses: equipMods.attributeBonuses,       // 自属性ダメージ+
      attributeResists: equipMods.attributeResists,       // 属性耐性
      attributeKillers: equipMods.attributeKillers,       // 属性特効

      // カテゴリ・派閥系
      categoryBonuses: equipMods.categoryBonuses,         // カテゴリブースト
      factionBonuses: equipMods.factionBonuses,           // 派閥ブースト

      // 状態異常系
      statusInflicts: equipMods.statusInflicts,
      statusResists: equipMods.statusResists,
      burnInflict: equipMods.burnInflict,
      poisonInflict: equipMods.poisonInflict,
      poisonDamage: equipMods.poisonDamage,
      paralyzeInflict: equipMods.paralyzeInflict,
      stunChance: equipMods.stunChance,
      instantDeathInflict: equipMods.instantDeathInflict,
      instantDeathResist: equipMods.instantDeathResist,

      // 行動系
      evasion: equipMods.evasion,
      preemptiveChance: equipMods.preemptiveChance,
      guaranteedFirst: equipMods.guaranteedFirst,
      cdReduce: equipMods.cdReduce,

      // 周回系
      expBonus: equipMods.expBonus,
      goldBonus: equipMods.goldBonus,
      dropRateBonus: equipMods.dropRateBonus,
      treasureFindBonus: equipMods.treasureFindBonus,
    },

    // 新スキルシステム対応
    skills: instance.skills.map(skill => ({
      ...skill,
      currentCd: skill.slotNumber === 3 ? (skill.initialCooldown || 3) : 0,
    })),

    // パッシブ情報
    passives: instance.passives?.filter(p => p.unlocked) || [],

    isAlive: true,
    isDefending: false,
  };
}

/**
 * 敵モンスターを生成（カードデータから）
 * @param {Object} card - カード情報
 * @param {number} level - 敵のレベル
 * @param {number} index - 敵のインデックス
 * @returns {Object} BattleUnit（敵用）
 */
export function createEnemyUnit(card, level, index) {
  // 敵は簡易的に計算（レアリティはRとして扱う）
  const stats = calculateAllStats(card, level, 'R', 0);

  // 敵のスキル（新システム）- レベル60相当で全スキル解放
  const skills = createSkillInstances(card, 60);

  // 敵のパッシブ（★3相当で全パッシブ解放）
  const passives = createPassiveInstances(card, 3);

  return {
    id: `enemy_${index}`,
    instanceId: `enemy_${card.id}_${index}`,
    name: card.name,
    isAlly: false,
    attribute: card.attribute,
    category: card.category || '',

    stats: {
      attack: stats.attack,
      maxHp: stats.maxHp,
      currentHp: stats.maxHp,
      defense: stats.defense,
      speed: stats.speed,
    },

    // 敵は全スキル使用可能
    skills: skills.map(skill => ({
      ...skill,
      currentCd: skill.slotNumber === 3 ? (skill.initialCooldown || 3) : 0,
      unlocked: true,
    })),

    // 敵のパッシブ（全解放）
    passives: passives.map(p => ({ ...p, unlocked: true })),

    isAlive: true,
    isDefending: false,
    level,
  };
}

// ============================================================
// 装備反映済みステータスの取得（表示用）
// ============================================================

/**
 * 装備効果を反映したステータスを取得（表示用）
 * バトルユニットへの変換を行わず、純粋にステータス値のみを返す
 *
 * @param {Object} instance - MonsterInstance
 * @returns {Object} 装備効果反映済みのstats
 */
export function getEquipmentModifiedStats(instance) {
  if (!instance?.stats) {
    return { attack: 0, maxHp: 0, currentHp: 0, defense: 0, speed: 0 };
  }

  // 装備によるステータス補正を計算
  const equipMods = equipmentManager.calculateEquipmentModifiers(instance);

  // 基礎ステータス + 装備ボーナス
  const baseAttack = instance.stats.attack;
  const baseMaxHp = instance.stats.maxHp;
  const baseDefense = instance.stats.defense;
  const baseSpeed = instance.stats.speed;

  const finalAttack = Math.floor(baseAttack * (1 + equipMods.atkPercent) + equipMods.atkFlat);
  const finalMaxHp = Math.floor(baseMaxHp * (1 + equipMods.hpPercent) + equipMods.hpFlat);
  const finalDefense = baseDefense + equipMods.defFlat;
  const finalSpeed = baseSpeed + equipMods.spdFlat;

  // 現在HPを最大HPに比例して調整
  const hpRatio = instance.stats.currentHp / baseMaxHp;
  const finalCurrentHp = Math.min(Math.floor(finalMaxHp * hpRatio), finalMaxHp);

  return {
    attack: finalAttack,
    maxHp: finalMaxHp,
    currentHp: finalCurrentHp,
    defense: finalDefense,
    speed: finalSpeed,
  };
}

// ============================================================
// 装備者本人へのブースト適用
// ============================================================

/**
 * 各ユニットに自身の装備のcategory_boost/faction_boostを適用
 *
 * 装備者本人が対象カテゴリ/派閥の場合のみ、自分自身にATK/HPブーストを適用。
 * パーティ内の他のユニットには影響しない。
 *
 * 注意: 設計書により、ブーストはATK/HPのみ上昇（DEF/SPDは変化なし）
 *
 * @param {Array} battleUnits - toBattleUnit()で作成されたバトルユニット配列
 * @returns {Array} ブースト適用済みのバトルユニット配列
 */
export function applyPartyBoosts(battleUnits) {
  if (!battleUnits || battleUnits.length === 0) return battleUnits;

  // 各ユニットに自分の装備のブースト効果を適用
  return battleUnits.map((unit) => {
    const categoryBonuses = unit.equipmentMods?.categoryBonuses || {};
    const factionBonuses = unit.equipmentMods?.factionBonuses || {};

    // ブースト効果がなければそのまま返す
    const hasBoosts =
      Object.keys(categoryBonuses).length > 0 ||
      Object.keys(factionBonuses).length > 0;
    if (!hasBoosts) return unit;

    let atkBoost = 0;
    let hpBoost = 0;

    const category = unit.category || '';

    // 自分の装備のカテゴリブーストを自分に適用
    for (const [groupName, boostValue] of Object.entries(categoryBonuses)) {
      if (isCategoryInGroup(category, groupName)) {
        atkBoost += boostValue;
        hpBoost += boostValue;
      }
    }

    // 自分の装備の派閥ブーストを自分に適用
    for (const [factionName, boostValue] of Object.entries(factionBonuses)) {
      if (isFactionInGroup(category, factionName)) {
        atkBoost += boostValue;
        hpBoost += boostValue;
      }
    }

    // ブーストがなければそのまま返す
    if (atkBoost === 0 && hpBoost === 0) return unit;

    // ブーストを適用（ATK/HPのみ）
    const newAttack = Math.floor(unit.stats.attack * (1 + atkBoost));
    const newMaxHp = Math.floor(unit.stats.maxHp * (1 + hpBoost));
    const hpRatio = unit.stats.currentHp / unit.stats.maxHp;
    const newCurrentHp = Math.floor(newMaxHp * hpRatio);

    return {
      ...unit,
      stats: {
        ...unit.stats,
        attack: newAttack,
        maxHp: newMaxHp,
        currentHp: newCurrentHp,
        // defense, speed は変更なし（SPDはカード基礎値から算出された固定値、バトル中は再計算しない）
      },
      // ブースト適用情報を記録（デバッグ用）
      selfBoosts: {
        atkBoost,
        hpBoost,
      },
    };
  });
}
